import orderBy from 'lodash/orderBy';
import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { GlobalSearchItem } from 'store/global/types';
import { MedicationsListState } from './reducers';
import { Medication } from './types';

/**
 * Medications
 */
const dataSelector = (state: MedicationsListState) => state.data;

export const medicationsSelector = (state: RootState) => state.medications.medications;

export const medicationsLoadingSelector = createSelector(
  medicationsSelector,
  medications => medications.isFetching
);

export const medicationsErrorSelector = createSelector(
  medicationsSelector,
  medications => medications.error
);

export const medicationsDataSelector = createSelector(medicationsSelector, state =>
  orderBy(dataSelector(state), ['dateWritten'], ['desc'])
);

export const medicationsRequestStateSelector = createSelector(
  medicationsSelector,
  medications => medications.requestState
);

/**
 * Medication Details
 */
export const medicationDetailsSelector = (state: RootState) => state.medications.medicationDetails;

export const medicationDetailsLoadingSelector = createSelector(
  medicationDetailsSelector,
  medicationDetails => medicationDetails.isFetching
);

export const medicationDetailsErrorSelector = createSelector(
  medicationDetailsSelector,
  medicationDetails => medicationDetails.error
);

export const medicationDetailsDataSelector = createSelector(
  medicationDetailsSelector,
  medicationDetails => {
    if (!medicationDetails?.data) return undefined;

    if (Array.isArray(medicationDetails.data)) {
      // Edge case: probably came from a pre-expansion bookmark (or maybe deeplink?)
      //   that had no EMR context. Just grab the first emr's data.
      //   Note: this was an approved design choice.
      return medicationDetails.data[0];
    }

    return medicationDetails.data;
  }
);

export const medicationDetailsProviderIdSelector = createSelector(
  medicationDetailsDataSelector,
  data => data?.prescriber?.reference
);

/**
 * Renew Prescription
 */
export const prescriptionRenewalDataSelector = createSelector(
  medicationDetailsSelector,
  medicationDetails => medicationDetails.data
);

export const prescriptionRenewalErrorSelector = createSelector(
  medicationDetailsSelector,
  medicationDetails => medicationDetails.error
);

/**
 * Encounter Medications
 */

export const activeEncounterMedicationsSelector = (state: RootState) =>
  state.medications.activeEncounterMedications;

export const activeEncounterMedicationsLoadingSelector = createSelector(
  activeEncounterMedicationsSelector,
  activeEncounterMedications => activeEncounterMedications.isFetching
);

export const activeEncounterMedicationsErrorSelector = createSelector(
  activeEncounterMedicationsSelector,
  activeEncounterMedications => activeEncounterMedications.error
);

export const activeEncounterMedicationsDataSelector = createSelector(
  activeEncounterMedicationsSelector,
  activeEncounterMedications => activeEncounterMedications.data
);

/**
 * Share Medications
 */
export const shareMedicationsSelector = (state: RootState) => state.medications.shareMedications;

export const shareMedicationsDataSelector = createSelector(
  shareMedicationsSelector,
  shareMedications => shareMedications.data
);

export const shareMedicationsSendingSelector = createSelector(
  shareMedicationsSelector,
  shareMedications => shareMedications.isSending
);

/**
 * Download Medications
 */

export const medicationsDownloadsSelector = (state: RootState) =>
  state.medications.medicationsDownloads;

export const medicationsDownloadingSelector = createSelector(
  medicationsDownloadsSelector,
  medicationsDownloads => medicationsDownloads.isDownloading
);

export const medicationsDownloadErrorSelector = createSelector(
  medicationsDownloadsSelector,
  medicationsDownloads => medicationsDownloads.error
);

export const searchFormattedMedications = createSelector(medicationsDataSelector, data => {
  return data.map(
    (item: Medication): GlobalSearchItem => {
      const type = item?.type || '';
      return {
        title: item?.name,
        provider: item?.prescriber?.name,
        location: item?.routingPharmacy?.routingPharmacyName,
        date: item?.dateWritten,
        category: 'Medications',
        icon: 'PillBottle',
        keywords: ['medications', 'medicine', 'prescriptions'],
        data: item,
        navWebPath: `/u/health-record/${type.toLowerCase()}-medications/medications/${item?.id}`
      };
    }
  );
});
